import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { dropdownAnimation } from '@nx-ratenow/core/animations';
import { ConfigService } from '@nx-ratenow/core/config';
import { NavigationService } from '@nx-ratenow/shared/data-access-utils';
import { AplicationTypeName } from '@nx-ratenow/shared/enums';
import { NavigationDropdown, NavigationItem, NavigationLink, NavigationSubheading } from '@nx-ratenow/shared/models';
import { cloneDeep } from 'lodash-es';
import { distinctUntilChanged, filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nx-ratenow-ui-sidenav-item',
  templateUrl: './ui-sidenav-item.component.html',
  styleUrls: ['./ui-sidenav-item.component.scss'],
  animations: [dropdownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSidenavItemComponent implements OnInit, OnChanges {
  _item!: NavigationItem;
  _isCollapsed = false;

  get item(): NavigationItem {
    return this._item;
  }

  @Input() set item(value: NavigationItem) {
    this._item = cloneDeep(value);
  }

  @Input() level!: number;
  isActive!: boolean;

  get isCollapsed(): boolean {
    return this._isCollapsed;
  }

  @Input() set isCollapsed(value: boolean) {
    this._isCollapsed = value;

    if (value && this.isDropdown(this.item)) {
      this.item.isOpen = false;
    }
  }

  @HostBinding('class')
  get levelClass() {
    return `item-level-${this.level}`;
  }

  constructor(
    private router: Router,
    private cd: ChangeDetectorRef,
    private navigationService: NavigationService,
    private configService: ConfigService
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter(() => this.isDropdown(this.item as NavigationDropdown)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.onRouteChange();
      });

    this.navigationService.openChange$
      .pipe(
        filter(() => this.isDropdown(this.item as NavigationDropdown)),
        untilDestroyed(this),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((item) => {
        this.onOpenChange(item);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      // eslint-disable-next-line no-prototype-builtins
      changes.hasOwnProperty('item') &&
      this.isDropdown(this.item as NavigationDropdown)
    ) {
      this.onRouteChange();
    }
  }

  onRouteChange() {
    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      this.isActive = true;
      this.cd.markForCheck();
    } else {
      this.isActive = false;
      this.cd.markForCheck();
    }
  }

  onOpenChange(item: NavigationDropdown) {
    if (this.isChildrenOf({ parent: this.item as NavigationDropdown, item })) {
      return;
    }

    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      return;
    }

    // if (this.item !== item) {
    //   debugger;
    //   (this.item as NavigationDropdown).isOpen = false;
    //   this.cd.markForCheck();
    // }
  }

  toggleOpen() {
    (this.item as NavigationDropdown).isOpen = !(this.item as NavigationDropdown).isOpen;

    this.navigationService.triggerOpenChange(this.item as NavigationDropdown);

    this.cd.markForCheck();

    // TODO: Need a better way to comunicate store with this service
    if (this.configService.aplicationName === AplicationTypeName.closeTheLoop && (this.item as NavigationDropdown).isOpen) {
      this.router.navigate([(this.item as NavigationDropdown).children[0].route]);
    }
  }

  isChildrenOf({ parent, item }: { parent: NavigationDropdown; item: NavigationDropdown }): any {
    if (parent.children.indexOf(item) !== -1) {
      return true;
    }

    return parent.children
      .filter((child) => this.isDropdown(child as NavigationDropdown))
      .some((child) => this.isChildrenOf({ parent: child as NavigationDropdown, item }));
  }

  // ---------------------------

  // TODO: Fix all this any
  hasActiveChilds(parent: NavigationDropdown): boolean {
    return parent.children.some((child: any) => {
      if (this.isDropdown(child)) {
        return this.hasActiveChilds(child);
      }

      if (this.isLink(child) && !this.isFunction((child as any).route as any)) {
        return this.router.isActive((child as any).route as string, false);
      }

      return false;
    });
  }

  isFunction(prop: NavigationLink['route']) {
    return prop instanceof Function;
  }

  onClickDropdown(item: any): void {
    item.route ? this.router.navigate([item.route]) : this.toggleOpen();
  }

  isDropdown(item: NavigationDropdown | any): item is NavigationDropdown {
    return item.type === 'dropdown';
  }

  isLink(item: NavigationItem): item is NavigationLink {
    return item.type === 'link';
  }

  isSubheading(item: NavigationItem): item is NavigationSubheading {
    return item.type === 'subheading';
  }
}
